import { Reducer } from 'redux';

import { ChainsFromConfig } from 'config/configType';
import { CrossChainBridge } from 'config/constants';
import { changeUser } from 'reduxState/currentUser/currentUserReducer';
import { WalletAction } from 'reduxState/wallet/walletReducer';

import { PendingDepositItem } from './pendingDepositTypes';

// Actions

export type NewPending = {
  /** Source Chain Transaction hash */
  sourceTxId: string;
  quantity: string;
  sourceChain: ChainsFromConfig;
  bridge?: CrossChainBridge;
  vault: PendingDepositItem['vault'] | undefined;
};

export const addPendingDeposit = (deposit: NewPending) => ({
  type: 'ADD_PENDING_DEPOSIT' as const,
  deposit,
});

export const updatePendingDepositConfirmations = (
  txHash: string,
  confirmations: number
) => ({
  type: 'UPDATE_PENDING_DEPOSIT_CONFIRMATIONS' as const,
  txHash,
  confirmations,
});

export const updateBridgedPendingDeposit = ({
  txHash,
  forwarderTxId,
  bridgeTxId,
}: {
  txHash: string;
  // bridgeMessageNonce: number,
  forwarderTxId: string | undefined;
  /** Destination */
  bridgeTxId: string | undefined;
}) => ({
  type: 'UPDATE_BRIDGED_PENDING_DEPOSIT' as const,
  txHash,
  forwarderTxId,
  bridgeTxId,
  // bridgeMessageNonce,
});

export const clearAllPendingDeposits = () => ({
  type: 'CLEAR_ALL_PENDING_DEPOSITS' as const,
});

export const removePendingDeposit = (sourceTxHash: string) => ({
  type: 'REMOVE_PENDING_DEPOSIT' as const,
  sourceTxHash,
});

type PendingDepositsAction =
  | ReturnType<
      | typeof changeUser
      | typeof addPendingDeposit
      | typeof updatePendingDepositConfirmations
      | typeof removePendingDeposit
      | typeof updateBridgedPendingDeposit
      | typeof clearAllPendingDeposits
    >
  | WalletAction;

export const pendingDepositTabSyncedActions: PendingDepositsAction['type'][] = [
  'ADD_PENDING_DEPOSIT',
  'UPDATE_PENDING_DEPOSIT_CONFIRMATIONS',
  'CLEAR_ALL_PENDING_DEPOSITS',
  'REMOVE_PENDING_DEPOSIT',
];

// Reducer

type PendingDepositsState = {
  pendingTxHashes: string[] | undefined;
  pendingMap: {
    [txHash: string]: PendingDepositItem | undefined;
  };
  /** Confirmations on all chains (contains 2 tx for bridged deposits) */
  confirmationsCounts: {
    // -1 = error; 0 = pending; 1+ = mined
    [txHashOrBridgedTxHash: string]: number | undefined;
  };
};

const defaultState: PendingDepositsState = {
  pendingTxHashes: undefined,
  pendingMap: {},
  // Count is in separate object - it change often and can cause unnecessary re-render
  confirmationsCounts: {},
};

/**
 * @cached
 *
 * How pending reducer works:
 *  1. user initialize new deposit
 *  2. deposit is pushed to reducer (and stored in local storage)
 *  3a. deposit fails to mine -> user see error modal
 *  3b. deposit succesfully mines
 *  4b. user see deposit on balances history page
 *  5. Success or error confirmation with notification
 *  6a. On error: User can remove deposit from page by clicking "x"
 *  6b. On success: Deposit automatically removed from pending
 */
export const pendingDepositsReducer: Reducer<
  PendingDepositsState,
  PendingDepositsAction
> = (state = defaultState, action): PendingDepositsState => {
  switch (action.type) {
    case 'CLEAR_ALL_PENDING_DEPOSITS':
    case 'CHANGE_WALLET':
    case 'REMOVE_WALLET':
      return defaultState;

    case 'CHANGE_USER':
      return action.loggedIn === false ? defaultState : state;

    case 'ADD_PENDING_DEPOSIT':
      return {
        pendingTxHashes: [
          action.deposit.sourceTxId,
          ...(state.pendingTxHashes || []),
        ],
        pendingMap: {
          ...state.pendingMap,
          [action.deposit.sourceTxId]: {
            type: 'pendingDeposit',
            ...action.deposit,
            time: Date.now(),
          },
        },
        confirmationsCounts: {
          ...state.confirmationsCounts,
          [action.deposit.sourceTxId]: 0,
        },
      };

    case 'UPDATE_PENDING_DEPOSIT_CONFIRMATIONS': {
      return {
        ...state,
        confirmationsCounts:
          state.confirmationsCounts[action.txHash] !== action.confirmations
            ? {
                ...state.confirmationsCounts,
                [action.txHash]: action.confirmations,
              }
            : state.confirmationsCounts,
      };
    }

    case 'UPDATE_BRIDGED_PENDING_DEPOSIT': {
      const deposit = state.pendingMap[action.txHash];
      if (!deposit) {
        console.error('[pendingDeposits] Unknown tx hash to update');
        return state;
      }
      return {
        ...state,
        pendingMap: {
          ...state.pendingMap,
          [action.txHash]: {
            ...deposit,
            bridgeTxId: action.bridgeTxId,
            // bridgeMessageNonce: action.bridgeMessageNonce,
            forwarderTxId: action.forwarderTxId,
          },
        },
      };
    }

    case 'REMOVE_PENDING_DEPOSIT': {
      const deposit = state.pendingMap[action.sourceTxHash];

      const clonedMap = Object.assign({}, state.pendingMap);
      const clonedConfirmMap = Object.assign({}, state.confirmationsCounts);
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete clonedMap[action.sourceTxHash];
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete clonedConfirmMap[action.sourceTxHash];
      if (deposit?.bridgeTxId) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete clonedConfirmMap[deposit.bridgeTxId];
      }
      return {
        pendingTxHashes: state.pendingTxHashes?.filter(
          hash => action.sourceTxHash !== hash
        ),
        pendingMap: clonedMap,
        confirmationsCounts: clonedConfirmMap,
      };
    }

    default:
      return state;
  }
};
